import Navigation from "../components/Navigation";
import Seo from "../components/Seo";
import LogoStrip from "../components/LogoStrip";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Target,
  Search,
  RefreshCw,
  Presentation,
  Wrench,
  Users,
  ArrowRight,
} from "lucide-react";

// Cornerstone / Leistungsseite für die kommerzielle Query "KI-Berater Vertrieb".
// Deutsch hardcoded (wie die Insight-Artikel) — die prerenderte DE-Variante wird indexiert.
// Inhalt = echte E-E-A-T (Jahrzehnt Vertrieb), keine erfundenen Kundenzahlen.

const TITLE = "KI im Vertrieb: Beratung, Workshops & Umsetzung – Daaniyal Khan";
const DESCRIPTION =
  "KI-Beratung für den Vertrieb im DACH-Raum: GenAI und ChatGPT dort einsetzen, wo sie wirklich Umsatz bewegen — Lead-Priorisierung, Gesprächsvorbereitung, Follow-up. Keynotes, Workshops und Umsetzungsbegleitung von Daaniyal Khan, mit Schwerpunkt Finanz- und Versicherungsvertrieb.";

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "KI-Beratung für den Vertrieb",
  name: "KI im Vertrieb – Beratung, Workshops und Umsetzung",
  description: DESCRIPTION,
  provider: {
    "@type": "Person",
    name: "Daaniyal Khan",
    url: "https://www.daaniyalkhan.com",
    jobTitle: "KI-, Vertriebs- und Digitalisierungsberater",
  },
  areaServed: ["DE", "AT", "CH"],
  audience: {
    "@type": "Audience",
    audienceType:
      "Vertriebsorganisationen, Außendienst, Makler und Maklerpools, Finanz- und Versicherungsvertrieb",
  },
  url: "https://www.daaniyalkhan.com/ki-vertrieb",
};

const LEVERAGE = [
  {
    icon: Target,
    title: "Lead-Priorisierung",
    body: "KI lernt aus historischen Abschlussdaten, welche Leads wirklich Abschlusswahrscheinlichkeit haben — und lenkt die Verkaufszeit dorthin, wo sie sich lohnt. Nicht „mehr Leads“, sondern dieselbe Zeit auf bessere.",
  },
  {
    icon: Search,
    title: "Gesprächsvorbereitung",
    body: "Unternehmensprofil, aktuelle Trigger-Events, Gesprächsaufhänger und Einwand-Antworten in Minuten statt 30–60 Minuten pro Termin — und das bei jedem Gespräch, nicht nur den wichtigen.",
  },
  {
    icon: RefreshCw,
    title: "Nachbearbeitung & Follow-up",
    body: "Gesprächsnotizen strukturieren, Follow-up-Mails vorbereiten, Wiedervorlagen nie vergessen. Follow-up-Disziplin ist einer der unterschätztesten Umsatzhebel im B2B.",
  },
];

const FORMATS = [
  {
    icon: Presentation,
    title: "Keynote & Impuls",
    body: "Klarer Realitäts-Check statt Hype: wo KI im Vertrieb wirkt, wo sie Theater ist — für Führungskreis, Vertriebstagung oder Maklerveranstaltung.",
  },
  {
    icon: Users,
    title: "Workshop",
    body: "Praxis-Workshop für Vertriebsteams: konkrete Use-Cases, Prompts und Abläufe für die eigene Pipeline — direkt anwendbar.",
  },
  {
    icon: Target,
    title: "Strategie-Sparring",
    body: "Wo liegt für Ihre Vertriebsorganisation der größte Hebel? Priorisierte KI-Roadmap statt Tool-Sammlung.",
  },
  {
    icon: Wrench,
    title: "Umsetzungsbegleitung",
    body: "Vom ersten Use-Case bis zum laufenden Betrieb — mit Blick auf Prozess, Akzeptanz im Team und DACH-Rechtsrahmen.",
  },
];

const FAQ = [
  {
    q: "Ersetzt KI meine Verkäufer?",
    a: "Nein. KI ist ein Verstärker, kein Ersatz. Sie nimmt dem guten Verkäufer die Nebenarbeit ab — Recherche, Priorisierung, Dokumentation — und gibt ihm mehr Zeit für Gespräch, Beziehung und Abschluss.",
  },
  {
    q: "Funktioniert das auch im Versicherungs- und Finanzvertrieb?",
    a: "Das ist der Schwerpunkt. Außendienst, Makler und Maklerpools, Vertriebseinheiten im Finanz- und Versicherungsbereich im DACH-Raum — inklusive der regulatorischen Besonderheiten (z. B. bei Kaltakquise).",
  },
  {
    q: "Wo fängt man an?",
    a: "Nicht beim glänzendsten Tool, sondern beim größten Zeitfresser Ihres besten Verkäufers. Das ist der erste, akzeptierte Use-Case — und die Basis für alles Weitere.",
  },
];

const KiVertrieb = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Seo title={TITLE} description={DESCRIPTION} />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Zum Hauptinhalt springen
      </a>
      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Hero */}
          <header className="mb-16 max-w-3xl">
            <div className="text-sm text-[#C5A66A] uppercase tracking-wider font-semibold mb-4">
              KI-Beratung Vertrieb · DACH
            </div>
            <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-[#0F1B2D] mb-6 leading-tight">
              KI im Vertrieb: Beratung, die Umsatz bewegt — statt Tool-Listen zu verlängern
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Die meisten KI-Vertriebs-Versprechen sind übertrieben — und die
              meisten Verkäufer, die KI abtun, unterschätzen sie. Ich komme aus
              dem Vertrieb: über ein Jahrzehnt, von der ersten Kaltakquise bis
              zur Führung von Vertriebsorganisationen. Daraus zeige ich, wo KI im
              B2B-Vertrieb wirklich Umsatz bewegt — und wo sie teures Theater
              ist.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center bg-[#C5A66A] text-[#0F1B2D] px-7 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                Strategie-Gespräch buchen
                <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link
                to="/insights/ki-im-vertrieb"
                className="inline-flex items-center px-7 py-3 rounded-lg font-semibold text-[#0F1B2D] border border-gray-300 hover:border-[#C5A66A] transition-colors"
              >
                Den ausführlichen Artikel lesen
              </Link>
            </div>
          </header>

          {/* Für wen */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Für wen
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Vertriebsorganisationen aller Branchen — vom Finanz- und
              Versicherungsvertrieb (mein Schwerpunkt) über den B2B-Außendienst
              bis zu Makler-, Ausschließlichkeits- und Bankenvertrieb. Überall
              dort, wo erfahrene Verkäufer zu viel Zeit mit Nebenarbeit
              verbringen und zu wenig mit dem, was nur sie können: dem Abschluss.
            </p>
          </section>

          {/* Wo KI wirkt */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Wo KI im Vertrieb wirklich wirkt
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {LEVERAGE.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="bg-[#FAFAF8] rounded-2xl border border-gray-200 p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#C5A66A]" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">
                    {title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-gray-600">
              Die ganze Landkarte — inklusive der drei Felder, in denen KI im
              Vertrieb teures Theater ist —{" "}
              <Link
                to="/insights/ki-im-vertrieb"
                className="text-[#C5A66A] underline font-medium"
              >
                steht im ausführlichen Artikel
              </Link>
              .
            </p>
          </section>

          {/* Case Study */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-6">
              Aus der Praxis: KI-Vertriebsassistent für einen Versicherer
            </h2>
            <div className="bg-[#0F1B2D] rounded-2xl p-8 text-white">
              <div className="grid gap-8 md:grid-cols-3 mb-8">
                <div>
                  <div className="text-4xl font-bold text-[#C5A66A] mb-1">~4.000</div>
                  <div className="text-sm text-gray-300">
                    Vermittler mit Zugriff (plus weitere Nutzer)
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#C5A66A] mb-1">
                    2–8 Std → Sek.
                  </div>
                  <div className="text-sm text-gray-300">
                    Tarifauskunft: vorher Rückruf, jetzt sofort
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#C5A66A] mb-1">24/7</div>
                  <div className="text-sm text-gray-300">
                    verfügbar — auch direkt beim Kunden vor Ort
                  </div>
                </div>
              </div>
              <p className="text-gray-200 leading-relaxed mb-4">
                <span className="font-semibold text-white">Ausgangslage:</span>{" "}
                Produkt- und Tariffragen liefen über eine Support-Hotline mit reiner
                Rückruffunktion — Antwortzeiten von zwei bis acht Stunden, danach
                erst die inhaltliche Klärung.
              </p>
              <p className="text-gray-200 leading-relaxed mb-4">
                <span className="font-semibold text-white">Lösung:</span> ein
                KI-Assistent zu allen Produkten und Tarifen, mit dem
                Vertriebspartner jederzeit sprechen oder schreiben können — auch
                direkt im Kundentermin. Derselbe Assistent wurde zusätzlich dem
                internen Support bereitgestellt.
              </p>
              <ul className="space-y-2 text-gray-200">
                <li>
                  • Tarifauskunft sofort statt Warten auf Rückruf — der Support wird
                  spürbar entlastet.
                </li>
                <li>• Schnelleres, sichereres Onboarding neuer Vermittler.</li>
                <li>
                  • Weniger Falschaussagen im Kundentermin — Führungskräfte können
                  neuen Mitarbeitern früher vertrauen.
                </li>
                <li>
                  • Führungskräfte entlastet: keine Dauer-Produktfragen mehr, echter
                  Fokus auf Vertrieb.
                </li>
              </ul>
              <p className="mt-6 text-xs text-gray-400">
                Anonymisiert. Details auf Anfrage.
              </p>
            </div>
          </section>

          {/* Formate */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Formate der Zusammenarbeit
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {FORMATS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="flex gap-4 bg-white rounded-2xl border border-gray-200 p-6"
                >
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center">
                    <Icon size={24} className="text-[#C5A66A]" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#0F1B2D] mb-1">
                      {title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Warum DK */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Warum Daaniyal Khan
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Ich berate nicht über den Vertrieb — ich komme aus ihm. Über ein
              Jahrzehnt Vertriebs- und Digitalisierungserfahrung im DACH-Raum,
              mit Schwerpunkt auf der Finanz- und Versicherungsbranche. Diese
              Kombination ist selten: jemand, der sowohl die KI-Mechanik versteht
              als auch weiß, wie sich ein Verkaufsgespräch tatsächlich anfühlt.
              Deshalb scheitern die Projekte, die KI als Verkäufer-Ersatz denken,
              und gelingen die, die KI als Verstärker einsetzen.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mt-4">
              Zum Thema habe ich auch ein Buch geschrieben:{" "}
              <a
                href="https://www.amazon.de/dp/B0BTGKRW19"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C5A66A] underline"
              >
                „Künstliche Intelligenz &amp; ChatGPT – der Einsatz im
                Versicherungsvertrieb“
              </a>
              . Einordnung im Interview bei{" "}
              <a
                href="https://www.businesstalk-kudamm.com/videos/digitalisierung-von-finanz-und-versicherungsvertrieben-daaniyal-khan-barmenia-gruppe/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C5A66A] underline"
              >
                Businesstalk am Kudamm
              </a>
              . Alle Titel auf der{" "}
              <Link to="/buecher" className="text-[#C5A66A] underline">
                Bücher-Seite
              </Link>
              .
            </p>
          </section>

          {/* Partner & Referenzen */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-6">
              Partner &amp; Referenzen
            </h2>
            <LogoStrip />
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Häufige Fragen
            </h2>
            <div className="space-y-6">
              {FAQ.map(({ q, a }) => (
                <div key={q}>
                  <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">{q}</h3>
                  <p className="text-gray-600 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Verwandte Schwerpunkte */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Verwandte Schwerpunkte
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/ki-versicherung"
                className="inline-flex items-center px-5 py-3 rounded-lg font-semibold text-[#0F1B2D] border border-gray-300 hover:border-[#C5A66A] hover:text-[#C5A66A] transition-colors"
              >
                KI in der Versicherung
              </Link>
              <Link
                to="/management-dashboards"
                className="inline-flex items-center px-5 py-3 rounded-lg font-semibold text-[#0F1B2D] border border-gray-300 hover:border-[#C5A66A] hover:text-[#C5A66A] transition-colors"
              >
                Dashboards &amp; Berichtswesen
              </Link>
              <Link
                to="/buecher"
                className="inline-flex items-center px-5 py-3 rounded-lg font-semibold text-[#0F1B2D] border border-gray-300 hover:border-[#C5A66A] hover:text-[#C5A66A] transition-colors"
              >
                Bücher
              </Link>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4 text-[#C5A66A]">
              Wo liegt Ihr größter Hebel?
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Ein 90-Minuten-Gespräch klärt, an welcher Stelle KI in Ihrem
              Vertrieb am schnellsten Umsatz bewegt — und wo Sie sich das Geld
              sparen.
            </p>
            <Link
              to="/#contact"
              className="inline-block bg-[#C5A66A] text-[#0F1B2D] px-8 py-4 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
            >
              Kostenloses Erstgespräch
            </Link>
          </section>
        </div>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </div>
  );
};

export default KiVertrieb;
